import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import {
  Gauge, Upload, Plus, Target, TrendingUp, TrendingDown,
  AlertCircle, CreditCard, CheckCircle2, XCircle,
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const utilColor = (pct) => (pct == null ? 'text-zinc-400' : pct > 30 ? 'text-red-400' : pct >= 25 ? 'text-amber-400' : 'text-green-400');

const CreditIntelligence = () => {
  const [loading, setLoading] = useState(true);
  const [readiness, setReadiness] = useState(null);
  const [utilization, setUtilization] = useState(null);
  const [scoreLog, setScoreLog] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ score: '', source: '', report_date: '' });
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [r, u, s, a] = await Promise.all([
        axios.get(`${API}/credit/intel/readiness`, { headers: getAuthHeaders() }),
        axios.get(`${API}/credit/intel/utilization`, { headers: getAuthHeaders() }),
        axios.get(`${API}/credit/score-log`, { headers: getAuthHeaders() }),
        axios.get(`${API}/credit/intel/alerts`, { headers: getAuthHeaders() }),
      ]);
      setReadiness(r.data);
      setUtilization(u.data);
      setScoreLog(s.data.entries || []);
      setAlerts(a.data.alerts || []);
    } catch (e) {
      toast.error('Failed to load credit intelligence');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const uploadScore = async (file) => {
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const r = await axios.post(`${API}/credit/score-log/upload`, form, { headers: getAuthHeaders() });
      const e = r.data.entry;
      toast.success(`Parsed: score ${e.score} · ${e.source} · ${e.report_date} (${e.confidence || 'parsed'})`);
      fetchAll();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.parse_failed ? `${d.reason} — add it manually below` : (typeof d === 'string' ? d : 'Upload failed'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const addManual = async () => {
    try {
      await axios.post(`${API}/credit/score-log`, {
        score: Number(manual.score), source: manual.source,
        report_date: manual.report_date || undefined,
      }, { headers: getAuthHeaders() });
      toast.success('Score entry recorded');
      setShowManual(false);
      setManual({ score: '', source: '', report_date: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add entry');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  const st = readiness?.standing || {};
  const chartData = scoreLog.map((s) => ({
    date: s.report_date, score: s.score, source: s.source,
  }));

  return (
    <Layout>
      <div className="space-y-8" data-testid="credit-intelligence">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-2">CREDIT INTELLIGENCE</h1>
            <p className="text-muted-foreground text-sm" data-testid="credit-disclaimer">
              Real sources only: Plaid card data + your uploaded reports. {readiness?.disclaimer}
            </p>
          </div>
          <div className="flex gap-3">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,application/pdf"
              className="hidden" onChange={(e) => uploadScore(e.target.files?.[0])} data-testid="score-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-5 py-3 flex items-center gap-2 rounded font-semibold text-sm disabled:opacity-50"
              data-testid="upload-score-button">
              <Upload className="w-4 h-4" />{uploading ? 'Parsing…' : 'Upload Score Report'}
            </button>
            <button onClick={() => setShowManual(!showManual)}
              className="bg-secondary/50 hover:bg-secondary text-white px-4 py-3 flex items-center gap-2 rounded text-sm"
              data-testid="manual-score-button">
              <Plus className="w-4 h-4" />Manual
            </button>
          </div>
        </div>

        {showManual && (
          <div className="glass-card p-4 border border-white/10 flex flex-wrap items-end gap-3" data-testid="manual-score-form">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Score (300-850)</label>
              <input type="number" value={manual.score} onChange={(e) => setManual({ ...manual, score: e.target.value })}
                className="bg-secondary/30 border border-border rounded px-3 py-2 text-white w-32" data-testid="manual-score-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Source</label>
              <input value={manual.source} onChange={(e) => setManual({ ...manual, source: e.target.value })}
                placeholder="Experian / Navy Federal" className="bg-secondary/30 border border-border rounded px-3 py-2 text-white w-48" data-testid="manual-source-input" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Report date</label>
              <input type="date" value={manual.report_date} onChange={(e) => setManual({ ...manual, report_date: e.target.value })}
                className="bg-secondary/30 border border-border rounded px-3 py-2 text-white" data-testid="manual-date-input" />
            </div>
            <button onClick={addManual} className="bg-primary text-primary-foreground px-4 py-2 rounded text-sm font-semibold" data-testid="manual-save-button">Save</button>
          </div>
        )}

        {/* Readiness */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="glass-card p-6 border border-primary/30 bg-primary/5">
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="w-4 h-4 text-primary" />
              <span className="text-xs uppercase tracking-widest text-primary font-bold">Current Standing</span>
            </div>
            {st.latest_score ? (
              <>
                <p className="text-5xl font-bold font-mono text-white" data-testid="latest-score">{st.latest_score}</p>
                <p className="text-xs text-muted-foreground mt-2" data-testid="score-provenance">
                  {st.source}{st.score_model ? ` · ${st.score_model}` : ''} · {st.report_date}
                  {st.score_days_old != null && ` (${st.score_days_old}d old)`}
                </p>
              </>
            ) : (
              <p className="text-sm text-zinc-400" data-testid="no-score-yet">No score uploaded yet — no number will be guessed</p>
            )}
            <p className={`text-sm font-mono mt-3 ${utilColor(st.overall_utilization_pct)}`} data-testid="overall-utilization">
              {st.overall_utilization_pct != null
                ? `Overall utilization ${st.overall_utilization_pct}%`
                : `${st.card_count || 0} credit cards connected — link cards via Plaid on the Wealth page`}
            </p>
            {readiness?.trend && (
              <p className={`text-xs mt-1 flex items-center gap-1 ${readiness.trend.delta >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="score-trend">
                {readiness.trend.delta >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                {readiness.trend.delta >= 0 ? '+' : ''}{readiness.trend.delta} since {readiness.trend.from_date}
              </p>
            )}
          </div>

          <div className="glass-card p-6 border border-white/10">
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-xs uppercase tracking-widest text-green-400 font-bold">Helping</span>
                </div>
                <div className="space-y-1" data-testid="credit-helping">
                  {(readiness?.helping || []).length ? readiness.helping.map((h, i) => (
                    <p key={i} className="text-sm text-zinc-300">{h}</p>
                  )) : <p className="text-sm text-zinc-500">Nothing recorded yet</p>}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-red-400" />
                  <span className="text-xs uppercase tracking-widest text-red-400 font-bold">Hurting</span>
                </div>
                <div className="space-y-1" data-testid="credit-hurting">
                  {(readiness?.hurting || []).length ? readiness.hurting.map((h, i) => (
                    <p key={i} className="text-sm text-zinc-300">{h}</p>
                  )) : <p className="text-sm text-zinc-500">Nothing flagged</p>}
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card p-6 border border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-widest text-amber-400 font-bold">Highest-Impact Action This Month</span>
            </div>
            <p className="text-sm text-white leading-relaxed" data-testid="highest-impact-action">
              {readiness?.highest_impact_action}
            </p>
          </div>
        </div>

        {/* Alerts */}
        {alerts.length > 0 && (
          <div className="glass-card p-5 border border-red-400/30 bg-red-500/5" data-testid="credit-alerts">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-400" />
              <span className="text-xs uppercase tracking-widest text-red-400 font-bold">Active Alerts · {alerts.length}</span>
            </div>
            <div className="space-y-1">
              {alerts.map((a) => (
                <p key={a.id} className="text-sm text-zinc-300 font-mono">{a.detail}</p>
              ))}
            </div>
          </div>
        )}

        {/* Score history */}
        <div className="glass-card p-6 border border-white/10">
          <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Score History · {scoreLog.length} entr{scoreLog.length === 1 ? 'y' : 'ies'}</span>
          {chartData.length >= 2 ? (
            <div className="h-56 mt-4" data-testid="score-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                  <YAxis domain={['dataMin - 20', 'dataMax + 20']} stroke="#71717a" fontSize={11} />
                  <Tooltip contentStyle={{ background: '#18181b', border: '1px solid #3f3f46' }}
                    formatter={(v, n, p) => [`${v} (${p.payload.source})`, 'score']} />
                  <Line type="monotone" dataKey="score" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mt-3" data-testid="score-history-empty">
              {scoreLog.length === 1 ? 'One entry logged — the trend line starts with your second upload' : 'Upload your first monthly score report to start the trend'}
            </p>
          )}
          {scoreLog.length > 0 && (
            <div className="mt-4 space-y-1" data-testid="score-log-list">
              {[...scoreLog].reverse().map((s) => (
                <div key={s.id} className="text-sm text-zinc-300 font-mono flex flex-wrap gap-2">
                  <span className="text-white font-bold">{s.score}</span>
                  <span>· {s.source}</span>
                  {s.score_model && <span>· {s.score_model}</span>}
                  <span className="text-zinc-500">· {s.report_date}</span>
                  <span className="text-zinc-600">· {s.method === 'upload' ? `parsed from upload (${s.confidence || 'n/a'})` : 'manual entry'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Card utilization */}
        <div className="glass-card p-6 border border-white/10">
          <div className="flex items-center gap-2 mb-4">
            <CreditCard className="w-4 h-4 text-primary" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Card Utilization (live via Plaid)</span>
          </div>
          {(utilization?.cards || []).length === 0 ? (
            <p className="text-sm text-zinc-500" data-testid="no-cards">
              No credit cards connected yet. Connect them with the same secure Plaid flow on the Wealth page — utilization tracking turns on automatically.
            </p>
          ) : (
            <div className="space-y-3" data-testid="card-utilization-list">
              {utilization.cards.map((c, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="w-56 shrink-0">
                    <p className="text-sm text-white">{c.account_name} ···{c.mask}</p>
                    <p className="text-[11px] text-zinc-500">{c.institution_name}{c.is_overdue ? ' · OVERDUE' : ''}</p>
                  </div>
                  <div className="flex-1 h-2 bg-secondary/40 rounded overflow-hidden">
                    <div className={`h-full ${c.utilization_pct > 30 ? 'bg-red-500' : c.utilization_pct >= 25 ? 'bg-amber-500' : 'bg-green-500'}`}
                      style={{ width: `${Math.min(100, c.utilization_pct || 0)}%` }} />
                  </div>
                  <span className={`font-mono text-sm w-28 text-right ${utilColor(c.utilization_pct)}`}>
                    {c.utilization_pct != null ? `${c.utilization_pct}%` : 'no limit data'}
                  </span>
                  <span className="font-mono text-xs text-zinc-500 w-40 text-right">
                    ${c.balance?.toLocaleString()} {c.limit ? `/ $${c.limit.toLocaleString()}` : ''}
                  </span>
                </div>
              ))}
              {utilization.overall_utilization_pct != null && (
                <p className={`text-sm font-mono pt-2 border-t border-white/10 ${utilColor(utilization.overall_utilization_pct)}`}>
                  Overall: {utilization.overall_utilization_pct}% (${utilization.total_balance?.toLocaleString()} of ${utilization.total_limit?.toLocaleString()})
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CreditIntelligence;
